import { useMemo, useState } from 'react';
import { CreditCard, Copy, KeyRound, Plus, Save, ShieldCheck, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { useAdminTenants, useAdminTenantDetail } from '@/hooks/use-admin';
import {
  usePaymentProviderConfig,
  useSavePaymentProviderConfig,
  useDeletePaymentProviderConfig,
  usePaymentApiClients,
  useCreatePaymentApiClient,
  useRevokePaymentApiClient,
  type CreatedApiClient,
} from '@/hooks/use-admin-payment-providers';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { toast } from '@/stores/toast-store';

// Credential fields per provider — must match payments-api PROVIDER_CREDENTIAL_FIELDS.
const PROVIDERS: Record<string, { label: string; fields: { key: string; label: string; hint?: string }[] }> = {
  clover: {
    label: 'Clover',
    fields: [
      { key: 'apiKey', label: 'Ecommerce API token', hint: 'Private token — Account & Setup → API Tokens' },
      { key: 'pakmsKey', label: 'Public tokenization key (PAKMS)' },
    ],
  },
  stripe: {
    label: 'Stripe',
    fields: [{ key: 'secretKey', label: 'Secret key', hint: 'sk_live_… / sk_test_…' }],
  },
  istream_ach: {
    label: 'iStream ACH',
    fields: [
      {
        key: 'locationId',
        label: 'iStream Location/Entity ID',
        hint: 'Identifies this merchant on iStream (routes the location’s ACH)',
      },
      { key: 'apiToken', label: 'iStream API token', hint: 'Basic token — base64 of username:password' },
    ],
  },
};

const TENANT_WIDE = '__tenant_wide__';

export function AdminPaymentProvidersPage() {
  const [tenantId, setTenantId] = useState('');
  const [locationSel, setLocationSel] = useState<string>(TENANT_WIDE);
  const [provider, setProvider] = useState('clover');
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [label, setLabel] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data: tenantsData } = useAdminTenants({ perPage: 200 });
  const tenants = tenantsData?.data ?? [];
  const { data: tenantDetail } = useAdminTenantDetail(tenantId);
  const locations = (tenantDetail?.data?.locations ?? []) as { id: string; name: string }[];

  const locationId = locationSel === TENANT_WIDE ? null : locationSel;
  const { data: configData, isLoading } = usePaymentProviderConfig(tenantId, locationId);
  const saveMutation = useSavePaymentProviderConfig();
  const deleteMutation = useDeletePaymentProviderConfig();

  // API clients (client-credentials)
  const { data: clientsData } = usePaymentApiClients(tenantId, locationId);
  const clients = clientsData?.data ?? [];
  const createClient = useCreatePaymentApiClient();
  const revokeClient = useRevokePaymentApiClient();
  const [clientLabel, setClientLabel] = useState('');
  const [newSecret, setNewSecret] = useState<CreatedApiClient | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const activeProvider = configData?.active_card_provider;
  const configured = configData?.providers ?? [];
  const providerFields = useMemo(() => PROVIDERS[provider]?.fields ?? [], [provider]);

  const canSave = useMemo(
    () => !!tenantId && providerFields.every((f) => (creds[f.key] ?? '').trim().length > 0),
    [tenantId, providerFields, creds],
  );

  const handleSave = async () => {
    try {
      await saveMutation.mutateAsync({
        tenant_id: tenantId,
        location_id: locationId,
        provider,
        credentials: Object.fromEntries(providerFields.map((f) => [f.key, creds[f.key].trim()])),
        label: label.trim() || undefined,
        set_as_route: true,
      });
      toast.success(`${PROVIDERS[provider].label} configured and set as the active provider for its payment type`);
      setCreds({});
      setLabel('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save provider configuration');
    }
  };

  const handleDelete = async (prov: string) => {
    try {
      await deleteMutation.mutateAsync({ tenantId, locationId, provider: prov });
      toast.success(`${PROVIDERS[prov]?.label ?? prov} configuration removed`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove configuration');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleCreateClient = async () => {
    try {
      const created = await createClient.mutateAsync({
        tenant_id: tenantId,
        location_id: locationId,
        label: clientLabel.trim() || undefined,
      });
      setNewSecret(created);
      setClientLabel('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create client');
    }
  };

  const handleRevokeClient = async (id: string) => {
    try {
      await revokeClient.mutateAsync(id);
      toast.success('API client revoked');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke client');
    } finally {
      setConfirmRevoke(null);
    }
  };

  const copy = (text: string, label: string) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error('Copy failed'),
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment Providers"
        description="Configure which payment processor and credentials each location uses. Data is stored encrypted in the payments service."
      />

      {/* Scope pickers */}
      <div className="rounded-lg border border-border bg-white p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Business (tenant)</label>
            <select
              value={tenantId}
              onChange={(e) => {
                setTenantId(e.target.value);
                setLocationSel(TENANT_WIDE);
              }}
              className="w-full rounded border border-border bg-white px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            >
              <option value="">Select a business…</option>
              {tenants.map((t: { id: string; name: string }) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Location</label>
            <select
              value={locationSel}
              onChange={(e) => setLocationSel(e.target.value)}
              disabled={!tenantId}
              className="w-full rounded border border-border bg-white px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
            >
              <option value={TENANT_WIDE}>All locations (tenant-wide default)</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {tenantId && (
        <>
          {/* Current state */}
          <div className="rounded-lg border border-border bg-white p-5">
            <div className="mb-3 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Current configuration</h3>
            </div>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <>
                <p className="mb-4 text-sm text-foreground">
                  Active card provider:{' '}
                  <span className="rounded bg-blue-100 px-2 py-0.5 font-mono text-xs font-semibold text-blue-700">
                    {activeProvider ?? '—'}
                  </span>
                </p>
                {configured.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No provider credentials configured for this scope yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="py-2 pr-4 font-medium">Provider</th>
                          <th className="py-2 pr-4 font-medium">Credentials</th>
                          <th className="py-2 pr-4 font-medium">Label</th>
                          <th className="py-2 pr-4 font-medium">Updated</th>
                          <th className="py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {configured.map((c) => (
                          <tr key={c.provider} className="border-b border-border/60">
                            <td className="py-2.5 pr-4 font-medium capitalize text-foreground">
                              {PROVIDERS[c.provider]?.label ?? c.provider}
                            </td>
                            <td className="py-2.5 pr-4 font-mono text-xs text-muted-foreground">
                              {Object.entries(c.credentials)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join('  ·  ')}
                            </td>
                            <td className="py-2.5 pr-4 text-muted-foreground">{c.label ?? '—'}</td>
                            <td className="py-2.5 pr-4 text-muted-foreground">
                              {new Date(c.updated_at).toLocaleDateString()}
                            </td>
                            <td className="py-2.5 text-right">
                              <button
                                onClick={() => setConfirmDelete(c.provider)}
                                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" /> Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Configure form */}
          <div className="rounded-lg border border-border bg-white p-5">
            <div className="mb-4 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Configure a provider</h3>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Provider</label>
                <select
                  value={provider}
                  onChange={(e) => {
                    setProvider(e.target.value);
                    setCreds({});
                  }}
                  className="w-full rounded border border-border bg-white px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  {Object.entries(PROVIDERS).map(([key, p]) => (
                    <option key={key} value={key}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Label (optional)</label>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Fremont store merchant"
                  className="w-full rounded border border-border bg-white px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
              {providerFields.map((f) => (
                <div key={f.key} className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">{f.label}</label>
                  <input
                    type="password"
                    autoComplete="off"
                    value={creds[f.key] ?? ''}
                    onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))}
                    placeholder={f.hint ?? ''}
                    className="w-full rounded border border-border bg-white px-3 py-2 font-mono text-xs text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  {f.hint && <p className="mt-1 text-[11px] text-muted-foreground">{f.hint}</p>}
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-[11px] text-muted-foreground">
                Saving stores the credentials encrypted and routes this scope's card payments to{' '}
                {PROVIDERS[provider].label}.
              </p>
              <button
                onClick={handleSave}
                disabled={!canSave || saveMutation.isPending}
                className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saveMutation.isPending ? 'Saving…' : 'Save & activate'}
              </button>
            </div>
          </div>
          {/* API clients (client-credentials) */}
          <div className="rounded-lg border border-border bg-white p-5">
            <div className="mb-1 flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">API access (client credentials)</h3>
            </div>
            <p className="mb-4 text-xs text-muted-foreground">
              Issue a client ID &amp; secret for this scope. Clients exchange them at{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">
                POST /payments-api/v1/oauth/token
              </code>{' '}
              for a short-lived access token, then call the payments API with{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">
                Authorization: Bearer &lt;token&gt;
              </code>
              .
            </p>

            {/* One-time secret reveal */}
            {newSecret && (
              <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-4">
                <p className="mb-2 text-xs font-semibold text-amber-800">
                  Save this secret now — it will not be shown again.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="w-24 shrink-0 text-[11px] font-medium text-amber-800">Client ID</span>
                    <code className="flex-1 overflow-x-auto rounded border border-amber-200 bg-white px-2 py-1 font-mono text-xs">
                      {newSecret.client_id}
                    </code>
                    <button
                      onClick={() => copy(newSecret.client_id, 'Client ID')}
                      className="rounded p-1 hover:bg-amber-100"
                    >
                      <Copy className="h-3.5 w-3.5 text-amber-700" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-24 shrink-0 text-[11px] font-medium text-amber-800">Client secret</span>
                    <code className="flex-1 overflow-x-auto rounded border border-amber-200 bg-white px-2 py-1 font-mono text-xs">
                      {newSecret.client_secret}
                    </code>
                    <button
                      onClick={() => copy(newSecret.client_secret, 'Client secret')}
                      className="rounded p-1 hover:bg-amber-100"
                    >
                      <Copy className="h-3.5 w-3.5 text-amber-700" />
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setNewSecret(null)}
                  className="mt-3 text-xs font-medium text-amber-800 underline hover:text-amber-900"
                >
                  I've saved it — dismiss
                </button>
              </div>
            )}

            {/* Create */}
            <div className="mb-4 flex items-end gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  New client label (optional)
                </label>
                <input
                  value={clientLabel}
                  onChange={(e) => setClientLabel(e.target.value)}
                  placeholder="e.g. Fremont POS integration"
                  className="w-full rounded border border-border bg-white px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
              <button
                onClick={handleCreateClient}
                disabled={createClient.isPending}
                className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                {createClient.isPending ? 'Generating…' : 'Generate client'}
              </button>
            </div>

            {/* Existing */}
            {clients.length === 0 ? (
              <p className="text-sm text-muted-foreground">No API clients for this scope yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Client ID</th>
                      <th className="py-2 pr-4 font-medium">Label</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Last used</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr key={c.id} className="border-b border-border/60">
                        <td className="py-2.5 pr-4 font-mono text-xs text-foreground">{c.client_id}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground">{c.label ?? '—'}</td>
                        <td className="py-2.5 pr-4">
                          <span
                            className={
                              c.status === 'active'
                                ? 'rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700'
                                : 'rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500'
                            }
                          >
                            {c.status}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-muted-foreground">
                          {c.last_used_at ? new Date(c.last_used_at).toLocaleString() : 'never'}
                        </td>
                        <td className="py-2.5 text-right">
                          {c.status === 'active' && (
                            <button
                              onClick={() => setConfirmRevoke(c.id)}
                              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={!!confirmRevoke}
        title="Revoke API client"
        message="Revoke this client? Any integration using it will stop authenticating immediately."
        confirmLabel="Revoke"
        variant="danger"
        loading={revokeClient.isPending}
        onConfirm={() => {
          if (confirmRevoke) handleRevokeClient(confirmRevoke);
        }}
        onCancel={() => setConfirmRevoke(null)}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove provider configuration"
        message={`Remove the ${confirmDelete ? (PROVIDERS[confirmDelete]?.label ?? confirmDelete) : ''} credentials for this scope? Payments will fall back to the tenant/platform default.`}
        confirmLabel="Remove"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (confirmDelete) handleDelete(confirmDelete);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
